import { useEffect } from 'react'
import { useField } from 'formik'
import { Icon } from '@iconify/react'
import { CompanyLogo, useDebounceState } from '@pepper/ui'

import { useValidateDomain } from 'container/SEOTools/hooks/useValidateDomain'
import { extractHostName } from 'utils/functions/extractHostName'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { TextField } from 'components/FormFields/FormFields'
import Suffix from './Suffix'

import s from './DomainInput.module.css'

type DomainInputProps = {
  name: string
  type: 'main' | 'competitor'
  onValidationComplete?: (domain: string, isAccessible: boolean) => void
  disabled?: boolean
}

const DEBOUNCE_TIME = 1000

export const DomainInput = ({
  name,
  type,
  onValidationComplete,
  disabled,
}: DomainInputProps) => {
  const [field] = useField(name)
  const companyId = useSelectedCompanyId()
  const debouncedValue = useDebounceState<string>(field.value, DEBOUNCE_TIME)

  const {
    domainValidation,
    isLoading: isValidating,
    refetch: reValidate,
  } = useValidateDomain({
    companyId,
    url: debouncedValue,
    isAccessible: true,
    isQueryEnabled: !!companyId && !!debouncedValue,
  })

  const isAccessible = !!domainValidation?.[debouncedValue]?.is_accessible
  const isInaccessible = !isValidating && !isAccessible

  const isCompetitorDomain = type === 'competitor'

  useEffect(() => {
    if (!isValidating) {
      onValidationComplete?.(field.value, isAccessible)
    }
  }, [isAccessible, isValidating, field.value])

  useEffect(() => {
    if (debouncedValue.length > 2) {
      reValidate()
    }
  }, [debouncedValue, reValidate])

  return (
    <div className={s.wrapper} data-id="add-domain-input-fields">
      <TextField
        name={name}
        className={s.input}
        showError={false}
        fieldProps={{
          disabled,
          placeholder: isCompetitorDomain
            ? 'example.com/*'
            : 'domain.com, *.domain.com or domain.com/*',
          prefix: isAccessible && (
            <CompanyLogo
              companyName={extractHostName(debouncedValue)}
              className={s.logo}
            />
          ),
          suffix:
            debouncedValue.length === 0 ||
            isInaccessible ||
            isCompetitorDomain ? null : (
              <Suffix type={isAccessible ? 'checked' : 'loading'} />
            ),
        }}
        inputType="text"
      />
      {debouncedValue && isInaccessible && (
        <div className={s.notAccessibleDomain}>
          <Icon icon="lucide:x-circle" width="16" className={s.notAccessible} />
          Unable to reach this domain, please check for any mistakes.
        </div>
      )}
    </div>
  )
}
